import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  Bell,
  TriangleAlert,
  Package,
  FlaskConical,
  Ban,
  Truck,
  ArrowLeftRight,
  Inbox,
  Check,
  X,
  UserPlus,
  Clock,
  AlarmClock,
  Hourglass,
  type LucideIcon,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useInventory } from '@/contexts/InventoryContext'
import { useActionInbox, type ActionRequest } from '@/data/notifications/useActionRequests'
import {
  salesNotificationRoute,
  useSalesNotifications,
  type SalesNotification,
  type SalesNotificationType,
} from '@/data/notifications/useSalesNotifications'
import { productStatus, expiringWithin, expiredBatches } from '@/data/inventory/selectors'
import { locationById } from '@/data/inventory/locations'
import { cn } from '@/lib/utils'

type Tone = 'critical' | 'warning' | 'info'

interface Notice {
  id: string
  icon: LucideIcon
  tone: Tone
  title: string
  detail: string
  to: string
}

const toneClass: Record<Tone, string> = {
  critical: 'text-destructive bg-destructive-light',
  warning: 'text-warning bg-warning-light',
  info: 'text-primary bg-accent',
}

/** Icon + tone per sales alert kind. */
const salesAlertStyle: Record<SalesNotificationType, { icon: LucideIcon; tone: Tone }> = {
  LEAD_ASSIGNED: { icon: UserPlus, tone: 'info' },
  TASK_REMINDER: { icon: AlarmClock, tone: 'info' },
  TASK_OVERDUE: { icon: Clock, tone: 'critical' },
  STALE_LEAD: { icon: Hourglass, tone: 'warning' },
}

function parseContext(raw?: string | null): Record<string, unknown> {
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return {}
  }
}

/** Bell = the role's single action queue. Three sources that never overlap:
 *  (1) delegated action requests routed to a role the user holds (real, from the
 *  backend — with Fulfil/Dismiss), (2) sales alerts addressed to the signed-in rep
 *  (lead assigned, follow-up due, overdue task, stale lead — real, from the backend,
 *  clearing on read), and (3) ambient inventory alerts derived from live stock (the
 *  always-visible version of the Dashboard "needs attention" panel). */
export function NotificationsBell() {
  const navigate = useNavigate()
  const { products, batches, receipts, transfers } = useInventory()
  const { inbox, resolve, dismiss } = useActionInbox()
  const { unread: salesAlerts, markRead, markAllRead } = useSalesNotifications()

  const notices = useMemo<Notice[]>(() => {
    const list: Notice[] = []

    const out = products.filter((p) => productStatus(p, batches) === 'out')
    if (out.length) {
      list.push({
        id: 'out',
        icon: TriangleAlert,
        tone: 'critical',
        title: out.length === 1 ? `${out[0].name} is out of stock` : `${out.length} products out of stock`,
        detail: 'Reorder via Procurement',
        to: '/stock?status=out',
      })
    }

    const low = products.filter((p) => productStatus(p, batches) === 'low')
    if (low.length) {
      list.push({
        id: 'low',
        icon: Package,
        tone: 'warning',
        title: `${low.length} ${low.length === 1 ? 'item' : 'items'} low on stock`,
        detail: 'Below reorder point',
        to: '/stock?status=low',
      })
    }

    const exp = expiringWithin(batches, 30)
    if (exp.length) {
      list.push({
        id: 'expiring',
        icon: FlaskConical,
        tone: 'warning',
        title: `${exp.length} ${exp.length === 1 ? 'lot' : 'lots'} expiring within 30 days`,
        detail: 'Review the FEFO register',
        to: '/batches',
      })
    }

    const expired = expiredBatches(batches).filter((b) => b.quantity > 0)
    if (expired.length) {
      list.push({
        id: 'expired',
        icon: Ban,
        tone: 'critical',
        title: expired.length === 1 ? `${expired[0].batchNumber} expired, removed from available` : `${expired.length} lots expired & blocked`,
        detail: 'Write off to clear',
        to: '/batches',
      })
    }

    const drafts = receipts.filter((r) => r.status === 'draft')
    if (drafts.length) {
      list.push({
        id: 'inbound',
        icon: Truck,
        tone: 'info',
        title: drafts.length === 1 ? `${drafts[0].poRef ?? drafts[0].code} due to receive` : `${drafts.length} inbound receipts to confirm`,
        detail: 'Open inbound',
        to: '/receiving',
      })
    }

    const lastTransfer = transfers.find((t) => t.status === 'completed')
    if (lastTransfer) {
      list.push({
        id: 'transfer',
        icon: ArrowLeftRight,
        tone: 'info',
        title: `Transfer to ${locationById(lastTransfer.toLocationId)?.name} completed`,
        detail: lastTransfer.code,
        to: '/transfers',
      })
    }

    return list
  }, [products, batches, receipts, transfers])

  const count = notices.length + inbox.length + salesAlerts.length

  function onFulfil(req: ActionRequest) {
    const ctx = parseContext(req.context)
    void resolve(req.id)
    if (typeof ctx.route === 'string') navigate(ctx.route, { state: ctx })
  }

  /** Opening a sales alert clears it and jumps to the lead/task it is about. */
  function onOpenSalesAlert(n: SalesNotification) {
    void markRead(n.id)
    const route = salesNotificationRoute(n)
    if (route) navigate(route)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative w-9 h-9 rounded-full bg-accent/70 hover:bg-accent flex items-center justify-center border border-border transition-colors"
          aria-label={`Notifications (${count})`}
        >
          <Bell className="w-[17px] h-[17px] text-primary" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[9px] font-bold flex items-center justify-center border-2 border-background">
              {count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-accent/30">
          <span className="text-[14px] font-semibold text-foreground">Notifications</span>
          <span className="text-[11px] font-semibold text-muted-foreground bg-muted rounded-full px-2 py-0.5">{count} to action</span>
        </div>
        <div className="max-h-[440px] overflow-y-auto hide-scrollbar">
          {count === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          )}

          {/* Delegated action requests routed to a role you hold */}
          {inbox.length > 0 && (
            <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Inbox className="w-3 h-3" /> Action requests
            </div>
          )}
          {inbox.map((req) => (
            <div key={req.id} className="px-4 py-3 border-b border-border/60 last:border-0">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-primary bg-accent">
                  <Inbox className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-foreground">{req.title}</div>
                  {req.message && <div className="text-[11px] text-muted-foreground truncate">{req.message}</div>}
                  <div className="text-[11px] text-muted-foreground">Requested by {req.requestedByName ?? 'a colleague'}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => onFulfil(req)}
                      className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2.5 py-1 text-[11px] font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <Check className="w-3 h-3" /> Fulfil
                    </button>
                    <button
                      onClick={() => void dismiss(req.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-muted text-muted-foreground px-2.5 py-1 text-[11px] font-semibold hover:bg-muted/70 transition-colors"
                    >
                      <X className="w-3 h-3" /> Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Sales alerts addressed to this rep */}
          {salesAlerts.length > 0 && (
            <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
              <span className="flex items-center gap-1.5"><UserPlus className="w-3 h-3" /> Sales</span>
              <button
                onClick={() => void markAllRead()}
                className="text-[10px] font-semibold text-primary hover:underline normal-case tracking-normal"
              >
                Mark all read
              </button>
            </div>
          )}
          {salesAlerts.map((n) => {
            const style = salesAlertStyle[n.type] ?? { icon: Bell, tone: 'info' as Tone }
            const Icon = style.icon
            return (
              <button
                key={n.id}
                onClick={() => onOpenSalesAlert(n)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-black/[0.02] border-b border-border/60 last:border-0 transition-colors"
              >
                <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', toneClass[style.tone])}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-foreground">{n.title}</span>
                  {n.body && <span className="block text-[11px] text-muted-foreground truncate">{n.body}</span>}
                </span>
              </button>
            )
          })}

          {/* Ambient inventory alerts */}
          {notices.length > 0 && (inbox.length > 0 || salesAlerts.length > 0) && (
            <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Alerts
            </div>
          )}
          {notices.map((n) => {
            const Icon = n.icon
            return (
              <button
                key={n.id}
                onClick={() => navigate(n.to)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-black/[0.02] border-b border-border/60 last:border-0 transition-colors"
              >
                <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', toneClass[n.tone])}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-foreground">{n.title}</span>
                  <span className="block text-[11px] text-muted-foreground">{n.detail}</span>
                </span>
              </button>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
